import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Globe, Check } from "lucide-react";

const LANGS = [
  { code: "fr", label: "Français",  flag: "🇫🇷" },
  { code: "en", label: "English",   flag: "🇬🇧" },
  { code: "es", label: "Español",   flag: "🇪🇸" },
  { code: "it", label: "Italiano",  flag: "🇮🇹" },
  { code: "de", label: "Deutsch",   flag: "🇩🇪" },
  { code: "ar", label: "العربية",   flag: "🇸🇦" },
  { code: "zh-CN", label: "中文",     flag: "🇨🇳" },
  { code: "ja", label: "日本語",     flag: "🇯🇵" },
];

const COOKIE_NAME = "googtrans";
const STORE_KEY = "bk_lang";

function readCurrent() {
  try {
    const raw = document.cookie.split(";").map(s => s.trim())
      .find(c => c.startsWith(`${COOKIE_NAME}=`));
    if (raw) {
      const val = decodeURIComponent(raw.split("=")[1] || "");
      const parts = val.split("/");
      if (parts.length >= 3 && parts[2]) return parts[2];
    }
    return localStorage.getItem(STORE_KEY) || "fr";
  } catch {
    return "fr";
  }
}

function writeCookie(lang) {
  const val = `/fr/${lang}`;
  // Set on current host AND on .host so subdomains + preview + prod work.
  document.cookie = `${COOKIE_NAME}=${val};path=/;max-age=31536000`;
  try {
    const host = window.location.hostname;
    // Also set with a dot-prefixed apex for TLD-wide persistence
    const parts = host.split(".");
    if (parts.length >= 2) {
      const dotDomain = "." + parts.slice(-2).join(".");
      document.cookie = `${COOKIE_NAME}=${val};path=/;domain=${dotDomain};max-age=31536000`;
    }
  } catch { /* silent */ }
  try { localStorage.setItem(STORE_KEY, lang); } catch { /* silent */ }
}

function clearCookie() {
  document.cookie = `${COOKIE_NAME}=;path=/;max-age=0`;
  try {
    const host = window.location.hostname;
    const parts = host.split(".");
    if (parts.length >= 2) {
      const dotDomain = "." + parts.slice(-2).join(".");
      document.cookie = `${COOKIE_NAME}=;path=/;domain=${dotDomain};max-age=0`;
    }
  } catch { /* silent */ }
  try { localStorage.removeItem(STORE_KEY); } catch { /* silent */ }
}

/**
 * Custom language selector wired to Google Website Translator.
 * Google Translate is loaded once from index.html; we just flip the
 * `googtrans` cookie and reload so it applies globally to every page,
 * including AI-generated content and product descriptions.
 */
export const LanguageSelector = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("fr");
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    setCurrent(readCurrent());
  }, []);

  useEffect(() => {
    if (!open) return;
    const b = btnRef.current?.getBoundingClientRect();
    if (b) {
      setMenuPos({
        top: b.bottom + 8,
        right: Math.max(12, window.innerWidth - b.right),
      });
    }
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)
          && btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const select = (code) => {
    if (code === current) { setOpen(false); return; }
    if (code === "fr") {
      clearCookie();
    } else {
      writeCookie(code);
    }
    setCurrent(code);
    // Force a hard reload so Google Translate re-injects with the new lang.
    window.location.reload();
  };

  const cur = LANGS.find((l) => l.code === current) || LANGS[0];

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((s) => !s)}
        aria-label="Sélectionner la langue"
        aria-haspopup="listbox"
        aria-expanded={open}
        className="lang-switch"
        data-testid="lang-switch-btn"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          height: 40,
          background: "transparent",
          border: "1px solid transparent",
          borderRadius: 999,
          color: "#4A2C5A",
          fontSize: 13,
          fontFamily: "'Poppins', system-ui, sans-serif",
          cursor: "pointer",
          transition: "background .2s, border-color .2s",
        }}
      >
        <Globe size={16} strokeWidth={1.5} />
        <span style={{ fontWeight: 500, letterSpacing: ".01em" }}
              className="notranslate">
          {cur.code === "zh-CN" ? "ZH" : cur.code.toUpperCase().slice(0, 2)}
        </span>
      </button>

      {open && createPortal(
        <ul
          ref={menuRef}
          role="listbox"
          aria-label="Langues"
          data-testid="lang-switch-menu"
          className="notranslate"
          style={{
            position: "fixed",
            top: menuPos.top,
            right: menuPos.right,
            zIndex: 100,
            listStyle: "none",
            margin: 0,
            padding: 6,
            minWidth: 200,
            background: "#FFFFFF",
            border: "1px solid #E5D6E3",
            borderRadius: 14,
            boxShadow: "0 20px 40px -12px rgba(74,44,90,0.22), 0 4px 10px rgba(45,27,46,0.06)",
            fontFamily: "'Poppins', system-ui, sans-serif",
            fontSize: 13.5,
          }}
        >
          {LANGS.map((l) => {
            const active = l.code === current;
            return (
              <li key={l.code}>
                <button
                  onClick={() => select(l.code)}
                  aria-selected={active}
                  data-testid={`lang-option-${l.code}`}
                  style={{
                    all: "unset",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: 10,
                    color: active ? "#9B4A6B" : "#2D1B2E",
                    fontWeight: active ? 600 : 500,
                    background: active ? "#FDF0F5" : "transparent",
                    transition: "background .15s",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = "#FAF3F7";
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>{l.flag}</span>
                  <span style={{ flex: 1 }}>{l.label}</span>
                  {active && <Check size={14} strokeWidth={2.4} color="#9B4A6B" />}
                </button>
              </li>
            );
          })}
        </ul>,
        document.body
      )}
    </>
  );
};

export default LanguageSelector;
